import { Avatar, IconButton, Stack, Typography } from '@mui/material'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { MouseEvent } from 'react'

interface BlockContentProps {
  fileTypesAllowed: string[]
  onChooseFolder?: () => void
  onChooseFiles?: () => void
}

const iconButtonSx = {
  bgcolor: '#C9E8F2',
  '&:hover': { bgcolor: '#B5DFEC' },
}

const handleActionClick = (event: MouseEvent, onClick: () => void) => {
  event.stopPropagation()
  event.preventDefault()
  onClick()
}

const BlockContent = ({ fileTypesAllowed, onChooseFolder, onChooseFiles }: BlockContentProps) => {
  const allowedExtensions = (
    <Typography component="p" gutterBottom variant="caption" color="text.secondary">
      Only these file extensions are allowed: {fileTypesAllowed.join(', ')}
    </Typography>
  )

  if (!onChooseFolder || !onChooseFiles) {
    return (
      <Stack px={2} py={4} alignItems="center" justifyContent="center" sx={{ width: 1, textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: '#C9E8F2' }}>
          <CloudUploadOutlinedIcon sx={{ color: 'primary.light' }} />
        </Avatar>
        <Typography variant="body2" mb={0.5} mt={1}>
          <Typography variant="subtitle2" component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
            Click to upload
          </Typography>
          &nbsp;or drag and drop
        </Typography>
        {allowedExtensions}
      </Stack>
    )
  }

  return (
    <Stack px={2} py={4} alignItems="center" spacing={2.5} sx={{ width: 1, textAlign: 'center' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 6 }} alignItems="center">
        <Stack alignItems="center" spacing={1} flex={1}>
          <IconButton
            onClick={(event) => handleActionClick(event, onChooseFolder)}
            aria-label="Choose folder for upload"
            title="Upload folder"
            sx={{ ...iconButtonSx, width: 64, height: 64 }}
          >
            <FolderOpenOutlinedIcon sx={{ color: 'primary.main', fontSize: 36 }} />
          </IconButton>
          <Typography variant="subtitle1" color="text.primary" fontWeight={600}>
            Upload folder
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a folder containing the imaging dataset.
          </Typography>
        </Stack>

        <Stack alignItems="center" spacing={1} flex={1}>
          <IconButton
            onClick={(event) => handleActionClick(event, onChooseFiles)}
            aria-label="Choose files for upload"
            title="Upload individual files"
            sx={{ ...iconButtonSx, width: 48, height: 48 }}
          >
            <UploadFileOutlinedIcon sx={{ color: 'primary.main', fontSize: 26 }} />
          </IconButton>
          <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
            Upload individual files
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add one or more files instead.
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Or drag and drop a folder or files here
        </Typography>
        {allowedExtensions}
      </Stack>
    </Stack>
  )
}

export default BlockContent
